/**
 * ChatZone — 메인 채팅 영역 컨테이너
 *
 * useAgentChat 호출, Chat UI 컴포넌트 조립. 에러 상태별 에러 컴포넌트 렌더링.
 * Artifact/우측 자세히보기 패널은 제거됨 — 위젯은 MessageList 내부에서 inline으로만 표시.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { usePersistentState } from "@/shared/components";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/shared/lib/utils";
import { useAgentChat } from "@/agent/state/use-agent-chat";
import { useAgentDefinition } from "@/agent/data/agents";
import { useAgentSession, createAgentSession } from "@/agent/data/sessions";
import { useChatHydration } from "@/agent/state/use-chat-hydration";
import {
  setPendingMessage,
  consumePendingMessage,
} from "@/agent/state/pending-messages";
import { agentKeys } from "@/api/hooks/query-keys";
import { ChatHeader } from "../../ui/chat/chat-header";
import { MessageList } from "../../ui/chat/message-list";
import { WelcomeMessage } from "../../ui/chat/welcome-message";
import { InputBar } from "../../ui/chat/input-bar";
import { ConversationHistory } from "../../ui/chat/conversation-history";
import { NetworkErrorBanner } from "../../ui/errors/network-error";
import { RateLimitError } from "../../ui/errors/rate-limit-error";
import { JwtExpiredError } from "../../ui/errors/jwt-expired-error";
import type { ChatMessage } from "@modootoday/core-agent/types";

const DEFAULT_AGENT_ID = "keyword-analyst";

/**
 * 낙관적 메시지 쌍 — 세션 생성 API 왕복 또는 remount 후 send() 발화까지의
 * 공백 구간 동안 렌더링된다. 사용자는 자신이 보낸 메시지와 "thinking..." 상태의
 * 어시스턴트 말풍선을 즉시 보게 된다. send() 가 실제 메시지로 state 를 채우면
 * 자동으로 교체된다 (messages.length > 0 체크).
 */
function buildOptimisticMessages(text: string): ChatMessage[] {
  const now = Date.now();
  return [
    {
      role: "user",
      id: "__optimistic_user__",
      content: text,
      timestamp: now,
    },
    {
      role: "assistant",
      id: "__optimistic_placeholder__",
      content: "",
      widgets: [],
      citations: [],
      suggestions: [],
      steps: [],
      segments: [],
      isStreaming: true,
      timestamp: now,
    },
  ];
}

export interface ChatZoneProps {
  agentId?: string;
  sessionId?: string;
  onDeepLink?: (path: string) => void;
  className?: string;
}

export function ChatZone({
  agentId: initialAgentId = DEFAULT_AGENT_ID,
  sessionId: initialSessionId,
  onDeepLink,
  className,
}: ChatZoneProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // agentId: props > localStorage (마지막 선택한 에이전트 복원) > DEFAULT
  const [agentId, setAgentId] = usePersistentState(
    "datalab_chat_agent_id",
    initialAgentId,
  );

  // pending-message consume: /chat 에서 handleSendFromFresh 가 세션 생성 후
  // setPendingMessage(sessionId, text) → navigate("/chat/$sessionId") 로 넘어오면
  // 새 ChatZone 이 mount되면서 이 값을 단 한 번 꺼내 auto-send 한다.
  // useRef 는 Strict Mode 에서도 재실행되지 않으므로 이 consume 은 정확히 1회 보장.
  const pendingConsumedRef = useRef(false);
  const autoSendMessageRef = useRef<string | null>(null);
  if (!pendingConsumedRef.current) {
    pendingConsumedRef.current = true;
    if (initialSessionId) {
      autoSendMessageRef.current =
        consumePendingMessage(initialSessionId) ?? null;
    }
  }
  const autoSendMessage = autoSendMessageRef.current;

  const [historyOpen, setHistoryOpen] = usePersistentState(
    "datalab_chat_history_open",
    true,
  );
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  // /chat 에서 handleSendFromFresh 진행 중인 메시지 — 낙관적 UI 표시용.
  // 사용자 클릭 즉시 setInFlightMessage(text) 하여 WelcomeMessage 대신
  // optimistic 한 user + thinking 말풍선을 렌더링. createAgentSession 실패 시 null로 복귀.
  const [inFlightMessage, setInFlightMessage] = useState<string | null>(null);

  // initialSessionId 진입 시 세션 메타 로드 → agentId 자동 동기화
  // /chat/:id로 직접 진입했을 때 해당 세션의 agent로 전환하여
  // 헤더/사이드바/input placeholder가 올바른 agent를 반영하도록 한다.
  const { data: initialSessionMeta } = useAgentSession(
    initialSessionId ?? null,
  );
  useEffect(() => {
    if (initialSessionMeta && initialSessionMeta.agentId !== agentId) {
      setAgentId(initialSessionMeta.agentId);
    }
  }, [initialSessionMeta?.agentId]);

  // historyOpen, agentId의 localStorage 영속은 usePersistentState가 자동 처리.

  const { data: agent, isLoading: agentLoading } = useAgentDefinition(agentId);

  const {
    messages,
    isStreaming,
    sessionId,
    error,
    send,
    stop,
    reset,
    hydrate,
  } = useAgentChat({
    agentId,
    initialSessionId,
    onError: (code) => {
      // RATE_LIMIT, AUTH_EXPIRED 에러는 UI에서 처리
      console.warn("[chat-zone] SSE error:", code);
    },
  });

  // DB 메시지 hydration — autoSendMessage 가 있을 때는 DB에 아직 메시지가 없고
  // 곧 send() 가 새로 채우므로 hydration 을 건너뛴다 (빈 배열로 clobber 방지).
  const {
    isLoading: isHydrating,
    isHydrated,
    loadedCount,
  } = useChatHydration({
    sessionId: autoSendMessage ? null : (initialSessionId ?? null),
    hydrate,
    isStreaming,
  });
  void isHydrated; // 향후 UI 표시에 사용 가능
  void loadedCount;

  // auto-send: pending 메시지가 있으면 mount 직후 정확히 1회 send() 호출.
  // ref 가드 + 빈 deps 로 strict mode 재실행을 방어한다.
  const autoSentRef = useRef(false);
  const sendRef = useRef(send);
  sendRef.current = send;
  useEffect(() => {
    if (autoSentRef.current) return;
    if (!autoSendMessage) return;
    autoSentRef.current = true;
    void sendRef.current(autoSendMessage);
  }, [autoSendMessage]);

  // 업프론트 세션 생성 플로우: /chat 에서 사용자가 starter/입력 submit 할 때 사용.
  // 1) 서버에 빈 세션 생성 요청 → server-authoritative sessionId 수신
  //    (클라이언트 UUID 는 조작 가능하므로 금지 — 반드시 서버 발급)
  // 2) pending-messages 스토어에 (sessionId, text) 저장
  // 3) /chat/$sessionId 로 navigate → chat_.$sessionId.tsx 가 remount 되면서
  //    ChatZone 이 consume 하고 auto-send useEffect 로 send(text) 1회 실행
  // 4) 이후 스트림은 새 ChatZone 인스턴스에서 안정적으로 진행 (mid-stream URL 변화 없음)
  const handleSendFromFresh = useCallback(
    async (text: string) => {
      if (isCreatingSession) return;
      // 클릭 즉시 낙관적 UI 노출 — 사용자가 체감하는 반응 지연 제거.
      setInFlightMessage(text);
      setIsCreatingSession(true);
      try {
        const newSessionId = await createAgentSession(agentId);
        setPendingMessage(newSessionId, text);
        queryClient.invalidateQueries({ queryKey: agentKeys.sessions() });
        void navigate({
          to: "/chat/$sessionId",
          params: { sessionId: newSessionId },
          replace: true,
        });
        // 성공 시에는 unmount 되므로 state 복구 불필요.
      } catch (err) {
        console.error("[chat-zone] createAgentSession failed:", err);
        setIsCreatingSession(false);
        setInFlightMessage(null);
      }
    },
    [agentId, isCreatingSession, queryClient, navigate],
  );

  // /chat (initialSessionId 없음) 에서는 업프론트 세션 생성 플로우를 쓰고,
  // /chat/:sessionId (세션 확보 상태) 에서는 기존 send 를 직접 호출한다.
  const isNewChatMode = !initialSessionId;
  const effectiveSend = isNewChatMode ? handleSendFromFresh : send;

  const handleNewChat = useCallback(() => {
    reset();
    // /chat 로 정식 navigate — ChatZone 이 remount 되지만 agentId 는 localStorage
    // (AGENT_ID_STORAGE_KEY) 로 복원되므로 방금 선택한 에이전트가 유지된다.
    void navigate({ to: "/chat" });
  }, [reset, navigate]);

  const handleSelectSession = useCallback(
    (sid: string) => {
      void navigate({ to: "/chat/$sessionId", params: { sessionId: sid } });
      setHistoryOpen(false);
    },
    [navigate],
  );

  const handleDeepLink = useCallback(
    (path: string) => {
      if (onDeepLink) {
        onDeepLink(path);
      } else {
        void navigate({ to: path as never });
      }
    },
    [onDeepLink, navigate],
  );

  // 에러 상태 처리
  const isRateLimit =
    error?.code === "RATE_LIMIT" || error?.code === "CONCURRENT_LIMIT";
  const isAuthExpired = error?.code === "AUTH_EXPIRED";
  const isTierLocked = error?.code === "TIER_LOCKED";
  const isNetworkError =
    error?.code === "INTERNAL" ||
    error?.code === "LLM_ERROR" ||
    error?.code === "TOOL_TIMEOUT";

  const hasMessages = messages.length > 0;
  // 기존 세션 진입 시 hydration 완료 전까지 Welcome을 숨김 (깜빡임 방지)
  const isHydratingSession =
    Boolean(initialSessionId) && isHydrating && !hasMessages;
  // Optimistic 메시지 텍스트:
  // - /chat 에서 handleSendFromFresh 진행 중: inFlightMessage 가 채워짐
  // - /chat/:sessionId 새 mount 직후 send() 발화 전: autoSendMessage 가 채워짐
  // 둘 중 하나라도 있고 real messages 가 아직 비었으면 optimistic UI 렌더.
  const pendingOptimisticText = inFlightMessage ?? autoSendMessage;
  const showOptimistic = !hasMessages && Boolean(pendingOptimisticText);
  const showWelcome =
    !hasMessages && !isStreaming && !isHydratingSession && !showOptimistic;
  const showLoadingSkeleton = isHydratingSession && !showOptimistic;
  // 낙관적 메시지는 한 번만 생성 (매 렌더마다 새 object 생성 방지)
  const optimisticMessages = showOptimistic
    ? buildOptimisticMessages(pendingOptimisticText!)
    : null;

  return (
    <div className={cn("flex flex-1 overflow-hidden", className)}>
      {/* 히스토리 사이드바 — md+ 에서 persistent, 토글 가능 */}
      {historyOpen && (
        <aside className="hidden md:flex w-64 shrink-0 border-r border-stroke-subtle">
          <ConversationHistory
            currentSessionId={sessionId}
            onSelectSession={handleSelectSession}
            onNewChat={handleNewChat}
            /* agentId 필터 제거 — 모든 agent의 세션을 통합 표시 */
            className="w-full"
          />
        </aside>
      )}

      {/* 메인 채팅 영역 */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* 네트워크 에러 배너 */}
        {isNetworkError && error && (
          <NetworkErrorBanner
            message={error.message}
            onRetry={() => {
              /* retry는 send()로 처리 */
            }}
          />
        )}

        {/* Chat Header */}
        <ChatHeader
          agentId={agentId}
          onAgentChange={(id) => {
            setAgentId(id);
            reset();
          }}
          sessionId={sessionId}
          isStreaming={isStreaming}
          onStop={stop}
          onNewChat={handleNewChat}
          onHistoryToggle={() => setHistoryOpen((prev: any) => !prev)}
          historyOpen={historyOpen}
        />

        {/* Rate Limit 에러 */}
        {isRateLimit && error && (
          <RateLimitError message={error.message} onDismiss={() => reset()} />
        )}

        {/* Tier 잠금 */}
        {isTierLocked && error && (
          <div className="mx-4 mt-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] text-amber-800 dark:border-amber-800/40 dark:bg-amber-950/20 dark:text-amber-300">
            <p className="font-medium">{error.message}</p>
          </div>
        )}

        {/* JWT 만료 */}
        {isAuthExpired && (
          <JwtExpiredError
            onLogin={() => void navigate({ to: "/auth/login" as never })}
          />
        )}

        {/* 메시지 영역 */}
        <div className="flex flex-1 overflow-hidden">
          {showLoadingSkeleton ? (
            <div className="flex flex-1 items-center justify-center">
              <div className="flex items-center gap-2 text-[12px] text-text-tertiary">
                <span className="size-3 animate-pulse rounded-full bg-accent/60" />
                이전 대화 불러오는 중...
              </div>
            </div>
          ) : optimisticMessages ? (
            /* Optimistic UI — 세션 생성 대기 or auto-send 발화 전 프레임 */
            <MessageList
              messages={optimisticMessages}
              isStreaming={true}
              onSend={effectiveSend}
              onDeepLink={handleDeepLink}
              className="flex-1"
            />
          ) : showWelcome ? (
            <WelcomeMessage
              agent={agent}
              onStarterClick={effectiveSend}
              onSelectSession={handleSelectSession}
              isLoading={agentLoading}
              className="flex-1"
            />
          ) : (
            <MessageList
              messages={messages}
              isStreaming={isStreaming}
              onSend={effectiveSend}
              onDeepLink={handleDeepLink}
              className="flex-1"
            />
          )}
        </div>

        {/* Input Bar */}
        <InputBar
          onSend={effectiveSend}
          onStop={stop}
          isStreaming={isStreaming || isCreatingSession}
          disabled={isRateLimit || isAuthExpired || isTierLocked}
          placeholder={
            isRateLimit
              ? "사용 한도에 도달했습니다..."
              : isAuthExpired
                ? "로그인이 필요합니다..."
                : isTierLocked
                  ? "플랜 업그레이드가 필요합니다..."
                  : `${agent?.name ?? "에이전트"}에게 질문하세요...`
          }
        />
      </div>
    </div>
  );
}
